import React, { useState } from 'react';
import {
  Box,
  Slider,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  RestartAlt as ResetIcon,
  SaveAlt as SaveIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { templatePoses } from '../data/templatePoses';

const PoseControls = ({ bones, onPoseChange }) => {
  const [poseData, setPoseData] = useState({});
  const [templateMenu, setTemplateMenu] = useState(null);
  const [filter, setFilter] = useState('');
  const [intensity, setIntensity] = useState(1); // 0..1 multiplier for template poses

  // Check if we have any valid bones
  const hasBones = Object.keys(bones).length > 0;

  // Normalize bone names similarly to ThreeDScene
  const normalizeBoneName = (name) => {
    if (!name) return name;
    let n = name;
    n = n.replace(/^mixamorig[:_]?/i, '');
    n = n.replace(/^Armature[|:_-]?/i, '');
    n = n.replace(/^Root[|:_-]?/i, '');
    n = n.replace(/[|.\s]+/g, '');
    return n;
  };

  // Heuristic grouping based on bone name
  const groupFor = (name) => {
    const n = name.toLowerCase();
    if (/(head|neck)/.test(n)) return 'Head';
    if (/(spine|chest|hips|pelvis|root)/.test(n)) return 'Spine';
    if (/(clavicle|shoulder|arm|forearm|hand|wrist|thumb|index|middle|ring|pinky)/.test(n)) {
      if (/left/.test(n) || /l(\b|[_-])/.test(n)) return 'Left Arm';
      if (/right/.test(n) || /r(\b|[_-])/.test(n)) return 'Right Arm';
      return 'Arms';
    }
    if (/(upleg|leg|foot|toe|ankle|thigh|calf)/.test(n)) {
      if (/left/.test(n) || /l(\b|[_-])/.test(n)) return 'Left Leg';
      if (/right/.test(n) || /r(\b|[_-])/.test(n)) return 'Right Leg';
      return 'Legs';
    }
    return 'Other';
  };

  const groupedBones = Object.keys(bones)
    .sort()
    .reduce((acc, boneName) => {
      if (filter && !boneName.toLowerCase().includes(filter.toLowerCase())) return acc;
      const group = groupFor(boneName);
      if (!acc[group]) acc[group] = [];
      acc[group].push(boneName);
      return acc;
    }, {});

  const handleRotationChange = (boneName, axis, value) => {
    const newPoseData = {
      ...poseData,
      [boneName]: {
        ...(poseData[boneName] || { x: 0, y: 0, z: 0 }),
        [axis]: value
      }
    };
    setPoseData(newPoseData);
    onPoseChange(newPoseData);
  };

  const resetBonePose = (boneName) => {
    const newPoseData = { ...poseData };
    newPoseData[boneName] = { x: 0, y: 0, z: 0 };
    setPoseData(newPoseData);
    onPoseChange(newPoseData);
  };

  const applyTemplatePose = (templatePoseData) => {
    const availableBones = new Set(Object.keys(bones));
    const scaled = {};
    const missing = [];
    Object.entries(templatePoseData).forEach(([boneName, rot]) => {
      const key = normalizeBoneName(boneName);
      if (availableBones.has(key)) {
        scaled[key] = {
          x: (rot.x || 0) * intensity,
          y: (rot.y || 0) * intensity,
          z: (rot.z || 0) * intensity,
        };
      } else {
        missing.push(boneName);
      }
    });

    // Merge with current pose; template values override provided bones only
    const newPoseData = {
      ...poseData,
      ...scaled,
    };

    if (missing.length) {
      // eslint-disable-next-line no-console
      console.warn('Template contained bones not found on this rig:', missing);
    }

    setPoseData(newPoseData);
    onPoseChange(newPoseData);
    setTemplateMenu(null);
  };

  const resetAllPoses = () => {
    const newPoseData = {};
    Object.keys(bones).forEach(boneName => {
      newPoseData[boneName] = { x: 0, y: 0, z: 0 };
    });
    setPoseData(newPoseData);
    onPoseChange(newPoseData);
  };

  if (!hasBones) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading character bones...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxHeight: '500px', overflowY: 'auto' }}>
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => setTemplateMenu(e.currentTarget)}
          startIcon={<CopyIcon />}
        >
          Template Poses
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={resetAllPoses}
          startIcon={<ResetIcon />}
        >
          Reset All
        </Button>
      </Box>

      {/* Simple filter/search */}
      <Box sx={{ mb: 2 }}>
        <input
          type="text"
          placeholder="Filter bones (e.g., arm, spine)"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)' }}
        />
      </Box>

      {/* Template intensity */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">Template Intensity</Typography>
        <Slider
          size="small"
          value={intensity}
          onChange={(_, v) => setIntensity(Array.isArray(v) ? v[0] : v)}
          valueLabelDisplay="auto"
          min={0}
          max={1}
          step={0.05}
        />
      </Box>

      <Menu
        anchorEl={templateMenu}
        open={Boolean(templateMenu)}
        onClose={() => setTemplateMenu(null)}
      >
        {Object.entries(templatePoses).map(([key, pose]) => (
          <MenuItem 
            key={key} 
            onClick={() => applyTemplatePose(pose.data)}
            sx={{ minWidth: 200 }}
          >
            <ListItemText 
              primary={pose.name}
              secondary={pose.description}
            />
          </MenuItem>
        ))}
      </Menu>

      {Object.entries(groupedBones).map(([groupName, boneNames]) => (
        <Accordion key={groupName}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{groupName}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {boneNames.map(boneName => bones[boneName] && (
              <Box key={boneName} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2">{boneName}</Typography>
                  <Tooltip title="Reset Bone">
                    <IconButton 
                      size="small" 
                      onClick={() => resetBonePose(boneName)}
                      sx={{ ml: 1 }}
                    >
                      <ResetIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {['x', 'y', 'z'].map(axis => (
                  <Box key={axis} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ minWidth: 20 }}>
                      {axis.toUpperCase()}
                    </Typography>
                    <Slider
                      size="small"
                      value={poseData[boneName]?.[axis] || 0}
                      onChange={(_, value) => handleRotationChange(boneName, axis, value)}
                      min={-180}
                      max={180}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                ))}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default PoseControls;
